import * as PIXI from 'pixi.js';
import { APP_WIDTH, APP_HEIGHT } from '../../utils';

import RoomContainer from '../containers/roomContainer';

const ROOMS_PER_PAGE = 4;

export default class RoomsManager {
    constructor() {
        this.rooms = [];
        this.page = 0;
        this.container = new PIXI.Container();
        this.container.label = 'roomsContainer';

        const loadingText = new PIXI.Text({
            text: 'Loading Rooms Page',
            style: {
                fontSize: 48,
                fill: 'white',
            }
        });
        loadingText.label = 'loadingText';
        loadingText.anchor.set(0.5);
        loadingText.x = APP_WIDTH / 2;
        loadingText.y = APP_HEIGHT / 2;
        this.container.addChild(loadingText);
    }

    attachToPixiApp(app) {
        app.stage.addChild(this.container);
    }

    detachFromPixiApp(app) {
        app.stage.removeChild(this.container);
    }

    async loadAssets() {
        // wait for all fonts to load
        await Promise.all([...(await document.fonts.ready)].map(font => font.load()));

        await PIXI.Assets.load([
            { src: '/assets/rooms_list/rooms_page_bg.png', alias: 'roomsPageBg' },
            { src: '/assets/rooms_list/rooms_list_bg.png', alias: 'roomsListBg' },
            { src: '/assets/rooms_list/room_card_bg.png', alias: 'roomCardBg' },
            { src: '/assets/rooms_list/create_room_button_bg.png', alias: 'createRoomButtonBg' },
            { src: '/assets/rooms_list/refresh_rooms_button_bg.png', alias: 'refreshRoomsButtonBg' },
            { src: '/assets/rooms_list/tall_brown_bar.png', alias: 'roomCardBrownInfoBar' },
            { src: '/assets/rooms_list/open_sign.png', alias: 'roomOpenSign' },
            { src: '/assets/rooms_list/no_player.png', alias: 'roomCardNoPlayer' },
            { src: '/assets/rooms_list/yes_player.png', alias: 'roomCardYesPlayer' },
            { src: '/assets/rooms_list/waiting_state.png', alias: 'roomCardWaitingState' },
            { src: '/assets/rooms_list/in_match_state.png', alias: 'roomCardInMatchState' },
            { src: '/assets/rooms_list/search_bar.png', alias: 'searchBarBg' },
            { src: '/assets/rooms_list/search_button.png', alias: 'searchButton' },
            { src: '/assets/rooms_list/page_left.png', alias: 'pageLeft' },
            { src: '/assets/rooms_list/page_right.png', alias: 'pageRight' },
            { src: '/assets/rooms_list/page_number.png', alias: 'pageNumber' },
            { src: '/assets/rooms_list/ropes_top.png', alias: 'ropesTop' },
            { src: '/assets/rooms_list/ropes_bottom.png', alias: 'ropesBottom' },
            { src: '/assets/rooms_list/decks_button.png', alias: 'decksButton' },
            { src: '/assets/rooms_list/decks_button_bg.png', alias: 'decksButtonBg' },
        ]);
    }

    removeLoadingText() {
        const loadingText = this.container.getChildByLabel('loadingText');
        if (!loadingText) return;
        
        this.container.removeChild(loadingText);
    }
    
    buildRoomsPage() {
        const roomsPageBg = new PIXI.Sprite(PIXI.Assets.get('roomsPageBg'));
        roomsPageBg.label = 'roomsPageBg';
        roomsPageBg.x = 0;
        roomsPageBg.y = 0;
        this.container.addChild(roomsPageBg);

        const ropesTop = new PIXI.Sprite(PIXI.Assets.get('ropesTop'));
        ropesTop.label = 'ropesTop';
        ropesTop.x = APP_WIDTH / 2;
        ropesTop.y = -360;
        ropesTop.anchor.set(0.5, 0);
        this.container.addChild(ropesTop);

        const ropesBottom = new PIXI.Sprite(PIXI.Assets.get('ropesBottom'));
        ropesBottom.label = 'ropesBottom';
        ropesBottom.x = APP_WIDTH / 2;
        ropesBottom.y = APP_HEIGHT + 80;
        ropesBottom.anchor.set(0.5, 1);
        this.container.addChild(ropesBottom);

        const roomsListBg = new PIXI.Sprite(PIXI.Assets.get('roomsListBg'));
        roomsListBg.label = 'roomsListBg';
        roomsListBg.x = APP_WIDTH / 2;
        roomsListBg.y = APP_HEIGHT / 2;
        roomsListBg.anchor.set(0.5);
        this.container.addChild(roomsListBg);

        this.buildButtons();
        this.buildSearchBar();
        this.buildPagination();
        this.buildDecksButton();
    }

    setRoomsList(rooms, page = 1) {
        this.rooms = rooms;
        this.page = page;

        this.clearRoomsList();
        this.buildRoomsList();
        this.buildPagination();
    }

    buildRoomsList() {
        const roomsInPage = this.rooms.slice((this.page - 1) * ROOMS_PER_PAGE, this.page * ROOMS_PER_PAGE);
        
        for (let i = 0; i < roomsInPage.length; i++) {
            const room = roomsInPage[i];
            const roomContainer = new RoomContainer(room);
            roomContainer.x = 118;
            roomContainer.y = 665 + (i * 250);
            this.container.addChild(roomContainer);

            roomContainer.eventMode = 'static';
            roomContainer.on('pointerdown', () => {
                console.log('room container clicked');
                this.onRoomContainerClick?.(room);
            });
        }
    }

    clearRoomsList() {
        for (const roomContainer of this.container.children.filter(child => child instanceof RoomContainer)) {
            roomContainer.destroy({ children: true });
        }
    }

    buildButtons() {
        // create room button
        const createRoomButton = new PIXI.Container();
        createRoomButton.label = 'createRoomButton';
        createRoomButton.x = 440;
        createRoomButton.y = 1972;
        this.container.addChild(createRoomButton);

        const createRoomButtonBg = new PIXI.Sprite(PIXI.Assets.get('createRoomButtonBg'));
        createRoomButtonBg.label = 'createRoomButtonBg';
        createRoomButtonBg.x = 0;
        createRoomButtonBg.y = 0;
        createRoomButtonBg.anchor.set(0.5);
        createRoomButton.addChild(createRoomButtonBg);

        createRoomButton.eventMode = 'static';
        createRoomButton.on('pointerdown', (event) => {
            event.stopPropagation();
            console.log('create room button clicked');
            this.onCreateRoomButtonClick?.();
        });

        // refresh rooms button
        const refreshRoomsButton = new PIXI.Container();
        refreshRoomsButton.label = 'refreshRoomsButton';
        refreshRoomsButton.x = 860;
        refreshRoomsButton.y = 1975;
        this.container.addChild(refreshRoomsButton);

        const refreshRoomsButtonBg = new PIXI.Sprite(PIXI.Assets.get('refreshRoomsButtonBg'));
        refreshRoomsButtonBg.label = 'refreshRoomsButtonBg';
        refreshRoomsButtonBg.x = 0;
        refreshRoomsButtonBg.y = 0;
        refreshRoomsButtonBg.anchor.set(0.5);
        refreshRoomsButton.addChild(refreshRoomsButtonBg);

        refreshRoomsButton.eventMode = 'static';
        refreshRoomsButton.on('pointerdown', (event) => {
            event.stopPropagation();
            console.log('refresh rooms button clicked');
            this.onRefreshRoomsButtonClick?.();
        });
    }

    buildSearchBar() {
        const searchBar = new PIXI.Container();
        searchBar.label = 'searchBar';
        searchBar.x = 122;
        searchBar.y = 472;
        this.container.addChild(searchBar);

        const searchBarBg = new PIXI.Sprite(PIXI.Assets.get('searchBarBg'));
        searchBarBg.label = 'searchBarBg';
        searchBarBg.x = 0;
        searchBarBg.y = 0;
        searchBar.addChild(searchBarBg);

        const searchButton = new PIXI.Sprite(PIXI.Assets.get('searchButton'));
        searchButton.label = 'searchButton';
        searchButton.x = 700;
        searchButton.y = 5;
        searchBar.addChild(searchButton);
    }

    buildPagination() {
        let pagination = this.container.getChildByLabel('pagination');
        if (pagination) {
            pagination.destroy({ children: true });
        }
        
        const numOfPages = Math.ceil(this.rooms.length / ROOMS_PER_PAGE);

        pagination = new PIXI.Container();
        pagination.label = 'pagination';
        pagination.x = APP_WIDTH / 2;
        pagination.y = 1734;
        this.container.addChild(pagination);

        const pageLeft = new PIXI.Sprite(PIXI.Assets.get('pageLeft'));
        pageLeft.label = 'pageLeft';
        pageLeft.x = -140;
        pageLeft.y = 0;
        pageLeft.anchor.set(1, 0.5);
        pagination.addChild(pageLeft);

        const pageRight = new PIXI.Sprite(PIXI.Assets.get('pageRight'));
        pageRight.label = 'pageRight';
        pageRight.x = 140;
        pageRight.y = 0;
        pageRight.anchor.set(0, 0.5);
        pagination.addChild(pageRight);

        const pageNumberBg = new PIXI.Sprite(PIXI.Assets.get('pageNumber'));
        pageNumberBg.label = 'pageNumberBg';
        pageNumberBg.x = 0;
        pageNumberBg.y = 0;
        pageNumberBg.anchor.set(0.5);
        pagination.addChild(pageNumberBg);

        const pageNumberText = new PIXI.Text({
            text: `${this.page} / ${numOfPages}`,
            style: {
                fontSize: 48,
                fill: 'white',
            },
        });
        pageNumberText.label = 'pageNumberText';
        pageNumberText.x = 0;
        pageNumberText.y = 0;
        pageNumberText.anchor.set(0.5);
        pagination.addChild(pageNumberText);

        pageLeft.eventMode = 'static';
        pageLeft.on('pointerdown', () => {
            console.log('page left clicked');
            if (this.page <= 1) return;

            this.setPage(this.page - 1);
        });

        pageRight.eventMode = 'static';
        pageRight.on('pointerdown', () => {
            console.log('page right clicked');
            if (this.page >= numOfPages) return;
            
            this.setPage(this.page + 1);
        });
    }

    buildDecksButton() {
        const decksButtonContainer = new PIXI.Container();
        decksButtonContainer.label = 'decksButtonContainer';
        decksButtonContainer.x = APP_WIDTH;
        decksButtonContainer.y = APP_HEIGHT - 130;
        this.container.addChild(decksButtonContainer);

        const decksButtonBg = new PIXI.Sprite(PIXI.Assets.get('decksButtonBg'));
        decksButtonBg.label = 'decksButtonBg';
        decksButtonBg.anchor.set(1, 0.5);
        decksButtonContainer.addChild(decksButtonBg);

        const decksButton = new PIXI.Sprite(PIXI.Assets.get('decksButton'));
        decksButton.label = 'decksButton';
        decksButton.x = -40;
        decksButton.y = -10;
        decksButton.anchor.set(1, 0.5);
        decksButtonContainer.addChild(decksButton);

        decksButtonContainer.eventMode = 'static';
        decksButtonContainer.on('pointerdown', () => {
            this.onDecksButtonClick?.();
        });
    }

    setPage(page) {
        this.page = page;

        this.clearRoomsList();
        this.buildRoomsList();
        this.buildPagination();
    }
}